import { kpiApi } from "@/api/kpiApi";
import Breadcrumbs from "@/components/Breadcrumbs";
import SearchIcon from "@mui/icons-material/Search";
import {
    Box,
    Button,
    CircularProgress,
    Container,
    Divider,
    IconButton,
    MenuItem,
    OutlinedInput,
    Pagination,
    Select,
    Typography,
} from "@mui/material";
import { parseInt } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { Outlet, Link as RouterLink, useSearchParams } from "react-router-dom";

// TODO: get kpi search filter...
//
// path: /api/v1/kpis/search-filter

/**
 * /workspace/kpis
 */
export default function KpiFinderLayout() {
    const [searchParams, setSearchParams] = useSearchParams();

    const [search, setSearch] = useState("");
    const [filter, setFilter] = useState({ kpi_type_id: "", group_id: "" });
    const [sort, setSort] = useState(["name", "-updated"]);
    const [page, setPage] = useState({ number: 1, size: 15 });

    const kpiOptions = kpiApi.endpoints.getKpiOptions.useQuery();
    const kpiResponsibleGroups = kpiApi.endpoints.getKpiResponsibleGroup.useQuery({
        is_active: true,
        page: { size: 1000 },
    });
    const [kpisTrigger, kpis] = kpiApi.endpoints.searchKpi.useLazyQuery({
        search,
        filter,
        sort,
        page,
    });
    const kpiData = useMemo(() => kpis.data?.data ?? [], [kpis.data]);

    const handleRefresh = () => kpisTrigger({ search, filter, sort, page });
    const handleOnChangePage = (event, page) => setPage((pre) => ({ ...pre, number: page }));

    // first load.
    useEffect(() => {
        handleRefresh();
    }, [page]);

    // reset page number to 1 when new data.
    useEffect(() => {
        setPage((prev) => ({ ...prev, number: 1 }));
    }, [filter]);

    // console.log(searchParams);

    return (
        <>
            <Container>
                <Breadcrumbs items={[{ label: "KPIs", to: "/workspace/kpis" }]} />
            </Container>

            <Container>
                <Box sx={{ display: "flex", flexDirection: "row" }}>
                    <Box>
                        <Typography variant="h1" fontSize={34} fontWeight="bold" gutterBottom>
                            KPIs
                        </Typography>
                    </Box>
                    <Box sx={{ marginLeft: "auto" }}>
                        <Button disabled={true} sx={{ textTransform: "none" }}>
                            Search
                        </Button>
                        <Button
                            component={RouterLink}
                            variant="contained"
                            color="primary"
                            to="/kpis/new"
                            sx={{ textTransform: "none" }}
                        >
                            New KPI
                        </Button>
                    </Box>
                </Box>

                <Box
                    sx={{
                        marginLeft: "auto",
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.4,
                        justifyContent: "flex-start",
                        alignItems: "center",
                    }}
                >
                    <OutlinedInput
                        name="search"
                        value={search}
                        placeholder="Search by name"
                        size="small"
                        onChange={(event) => setSearch(event.target.value)}
                        onKeyDown={(event) => event.key === "Enter" && handleRefresh()}
                    />
                    <Select
                        value={filter.kpi_type_id}
                        defaultValue=""
                        displayEmpty
                        name="type"
                        placeholder="ประเภทตัวชี้วัด"
                        onChange={(event) =>
                            setFilter((pre) => ({
                                ...pre,
                                kpi_type_id: event.target.value,
                            }))
                        }
                        size="small"
                    >
                        <MenuItem value="">All type</MenuItem>
                        {kpiOptions.data?.data?.kpiTypes?.map((type) => (
                            <MenuItem key={type.id} value={type.id}>
                                {type.title}
                            </MenuItem>
                        ))}
                    </Select>
                    <Select
                        value={filter.group_id}
                        defaultValue=""
                        displayEmpty
                        name="group"
                        placeholder="ผู้รับผิดชอบ"
                        onChange={(event) =>
                            setFilter((pre) => ({
                                ...pre,
                                group_id: event.target.value,
                            }))
                        }
                        size="small"
                    >
                        <MenuItem value="">All group</MenuItem>
                        {kpiResponsibleGroups.data?.data &&
                            [...kpiResponsibleGroups.data?.data]
                                .sort((groupA, groupB) => {
                                    return groupA.title.localeCompare(groupB.title);
                                })
                                .map((group) => (
                                    <MenuItem key={group.id} value={group.id}>
                                        {group.title}
                                    </MenuItem>
                                ))}
                    </Select>
                    <Select
                        value={sort.length == 1 ? sort[0] : ""}
                        defaultValue=""
                        displayEmpty
                        name="sort"
                        size="small"
                        onChange={(event) =>
                            setSort((pre) => {
                                if (event.target.value == "") return ["name", "-updated"];
                                return [event.target.value];
                            })
                        }
                    >
                        <MenuItem value="">Name, Last updated</MenuItem>
                        <MenuItem value="name">Name</MenuItem>
                        <MenuItem value="-name">Name, descending</MenuItem>
                        <MenuItem value="updated">Olest updated</MenuItem>
                        <MenuItem value="-updated">Last updated</MenuItem>
                    </Select>
                    <IconButton color="primary" onClick={handleRefresh}>
                        {kpis.isFetching ? <CircularProgress size="1em" /> : <SearchIcon />}
                    </IconButton>
                </Box>

                {/**
                <Tabs value={0} sx={{ borderBottom: 1, borderColor: "divider", flexWrap: "wrap" }}>
                    <Tab
                        label="All"
                        value={0}
                        // sx={{ textTransform: "none", display: "inline-flex" }}
                    />
                </Tabs>
                */}

                <Divider sx={{ marginY: 1 }} />
            </Container>

            {/** content */}
            <Container>
                <Box sx={{ maringY: 2 }}>total: {kpis.data?.meta?.total ?? "-"}</Box>

                <Outlet context={{ search, filter, sort, kpiData }} />

                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        marginTop: 3,
                    }}
                >
                    <Pagination
                        showFirstButton
                        showLastButton
                        // count={Math.ceil(parseInt(kpis?.data?.total) / parseInt(kpis?.data?.per_page) ?? 1)}
                        count={Math.ceil(parseInt(kpis.data?.meta.total ?? 1) / page.size)}
                        page={page.number}
                        onChange={handleOnChangePage}
                    />
                </Box>
            </Container>
        </>
    );
}
